import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ContactListItem, ListResult } from "@cx/contracts";
import { useSession } from "../../session";

/** Cliente/fornecedor do título: busca por nome ou CPF/CNPJ (usuário-vendedor só acha a própria carteira). */
export function ContactPicker({
  label,
  value,
  error,
  onChange,
  inputId = "title-contact-search",
  required = true,
}: {
  label: string;
  /** Duas buscas na mesma tela (ex.: cliente e "cobrar de" do contrato) precisam de ids diferentes. */
  inputId?: string;
  required?: boolean;
  value: { id: string; name: string } | null;
  error?: string;
  onChange(c: { id: string; name: string } | null): void;
}) {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["contacts", companyId, { q: term, picker: "title" }],
    enabled: !value && term.length >= 2,
    queryFn: () =>
      api<ListResult<ContactListItem>>(`/contacts?${new URLSearchParams({ q: term, limit: "8" })}`),
  });
  const star = required && (
    <span className="text-danger" aria-hidden="true">
      {" "}
      *
    </span>
  );
  if (value)
    return (
      <div className="mb-3">
        <span className="form-label d-block">
          {label}
          {star}
        </span>
        <p
          className="mb-0"
          data-testid={inputId === "title-contact-search" ? "title-contact" : `${inputId}-chosen`}
        >
          <strong>{value.name}</strong>{" "}
          <button type="button" className="btn btn-sm btn-link" onClick={() => onChange(null)}>
            Trocar
          </button>
        </p>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </div>
    );
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={inputId}>
        {label}
        {star}
      </label>
      <input
        id={inputId}
        type="search"
        className={`form-control${error ? " is-invalid" : ""}`}
        placeholder="Nome ou CPF/CNPJ"
        aria-invalid={error ? "true" : undefined}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Contatos encontrados">
          {found.isSuccess && found.data.data.length === 0 && (
            <li className="list-group-item text-muted">Nenhum contato encontrado.</li>
          )}
          {found.data?.data.map((c) => (
            <li key={c.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">{c.name}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Escolher ${c.name}`}
                onClick={() => {
                  onChange({ id: c.id, name: c.name });
                  setQ("");
                }}
              >
                Escolher
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
